/**
 * Client-side iCalendar parsing (ical.js for syntax only).
 *
 * `parseICalFeed` turns ICS text into cacheable `ICalEventItem`s in series space (see types.ts):
 *  - every TZID is resolved once to a valid IANA zone (timezoneResolver.ts) and converted with Intl;
 *    ical.js's own VTIMEZONE arithmetic is never used for conversion (ical.js #847),
 *  - floating timed events take the calendar's X-WR-TIMEZONE when it has one, else stay floating
 *    (shown at the same wall time wherever the viewer is),
 *  - all-day dates (VALUE=DATE, or Outlook's X-MICROSOFT-*-ALLDAYEVENT on midnight times) are
 *    floating dates and never shifted,
 *  - RRULEs are kept as text and expanded on the wall clock later (icalUtils.ts),
 *  - EXDATE / RDATE / RECURRENCE-ID are converted into the series' own time space so they match
 *    the expanded starts exactly; STATUS CANCELLED and CANCELED both cancel,
 *  - each VEVENT is isolated: one that cannot be read is reported in `skipped` and the rest of the
 *    feed survives, also when a block is not even valid iCalendar syntax.
 */

import ICAL from "ical.js"
import * as rruleModule from "rrule"
import type { ICalEventItem, SkippedEvent, SkipReason } from "./types"
import {
  createZoneResolver,
  resolveCalendarZone,
  tzidKey,
  type ResolvedZone,
  type ResolveContext,
  vtimezoneFromObservances,
  type VTimezoneInfo,
  type VTimezoneObservance,
  type ZoneSource,
} from "./timezoneResolver"
import {
  DAY_MS,
  instantToWall,
  viewerZone,
  wallStartOfDay,
  wallDayKey,
  wallToInstant,
} from "./zonedTime"

// rrule CJS/ESM interop: handle both Vite (named exports) and Node ESM (default.RRule)
const RRule: typeof rruleModule.RRule =
  (rruleModule as unknown as { default?: { RRule: typeof rruleModule.RRule } })
    .default?.RRule ?? rruleModule.RRule

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ICalParseErrorCode = "not-ical" | "parse"

/** The feed as a whole could not be read. */
export class ICalParseError extends Error {
  readonly code: ICalParseErrorCode
  constructor(code: ICalParseErrorCode, message: string) {
    super(message)
    this.name = "ICalParseError"
    this.code = code
  }
}

export interface ZoneReport {
  /** The TZID as written (or `X-WR-TIMEZONE` for floating events placed in the calendar's zone). */
  tzid: string
  zone: string
  source: ZoneSource
  uncertain: boolean
  /** Events whose DTSTART uses it. */
  events: number
}

export interface ParsedFeed {
  events: ICalEventItem[]
  /** X-WR-CALNAME (or NAME). */
  calendarName: string | null
  /** X-WR-TIMEZONE resolved to IANA, if any. */
  calendarZone: string | null
  skipped: SkippedEvent[]
  /** Events, a series counted once (modified instances not counted). */
  eventCount: number
  recurringCount: number
  zones: ZoneReport[]
}

export interface ParseOptions {
  /** The viewer's zone (defaults to the runtime's). */
  userZone?: string
  /** "Now" for the resolver's probe years. */
  now?: number
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** A date or date-time value as written. */
interface TimeValue {
  /** The written fields as pseudo-UTC ms. */
  wall: number
  isDate: boolean
  kind: "utc" | "zoned" | "floating"
  zone?: ResolvedZone
  tzid?: string
}

type Series =
  | { kind: "allday" }
  | { kind: "floating" }
  | { kind: "zoned"; zone: string; uncertain: boolean }

interface FeedContext {
  resolve: (tzid: string) => ResolvedZone
  calendarZone: string | null
  userZone: string
}

class SkipError extends Error {
  readonly reason: SkipReason
  constructor(reason: SkipReason) {
    super(reason)
    this.reason = reason
  }
}

function isTime(v: unknown): v is ICAL.Time {
  return !!v && typeof v === "object" && "isDate" in v && "year" in v
}

function readTime(prop: ICAL.Property, value: unknown, ctx: FeedContext): TimeValue {
  if (!isTime(value)) throw new SkipError("invalid-date")
  const wall = Date.UTC(
    value.year,
    value.month - 1,
    value.day,
    value.isDate ? 0 : value.hour,
    value.isDate ? 0 : value.minute,
    value.isDate ? 0 : value.second,
  )
  if (!Number.isFinite(wall)) throw new SkipError("invalid-date")
  if (value.isDate) return { wall, isDate: true, kind: "floating" }
  const param = prop.getParameter("tzid")
  const tzid = Array.isArray(param) ? param[0] : param
  if (typeof tzid === "string" && tzid.trim()) {
    return { wall, isDate: false, kind: "zoned", zone: ctx.resolve(tzid), tzid }
  }
  if (value.zone === ICAL.Timezone.utcTimezone || value.zone?.tzid === "UTC") {
    return { wall, isDate: false, kind: "utc" }
  }
  return { wall, isDate: false, kind: "floating" }
}

function instantOf(tv: TimeValue): number | null {
  if (tv.kind === "utc") return tv.wall
  if (tv.kind === "zoned") return wallToInstant(tv.wall, tv.zone!.zone)
  return null
}

/** A value in the series' own time space (ms): instants for zoned series, wall clock otherwise. */
function toSeriesMs(tv: TimeValue, series: Series, ctx: FeedContext): number {
  if (series.kind === "zoned") {
    return instantOf(tv) ?? wallToInstant(tv.wall, series.zone)
  }
  if (tv.kind === "floating") return tv.wall
  if (series.kind === "allday" && tv.kind === "zoned") return tv.wall
  const instant = instantOf(tv)!
  return instantToWall(
    instant,
    series.kind === "allday" ? (ctx.calendarZone ?? ctx.userZone) : ctx.userZone,
  )
}


function addExclusion(item: ICalEventItem, tv: TimeValue, series: Series, ctx: FeedContext) {
  if (series.kind === "allday" || tv.isDate) {
    const day = wallDayKey(tv.isDate ? tv.wall : toSeriesMs(tv, series, ctx))
    ;(item.exdateDays ??= []).push(day)
  } else {
    ;(item.exdates ??= []).push(Math.floor(toSeriesMs(tv, series, ctx) / 1000))
  }
}

function isMidnight(wall: number): boolean {
  return ((wall % DAY_MS) + DAY_MS) % DAY_MS === 0
}

function isTrueFlag(v: unknown): boolean {
  return typeof v === "string" && v.trim().toUpperCase() === "TRUE"
}

function stringProp(vevent: ICAL.Component, name: string): string | null {
  const v = vevent.getFirstPropertyValue(name)
  if (v === null || v === undefined) return null
  const s = String(v)
  return s === "" ? null : s
}

interface ReadEvent {
  item: ICalEventItem
  series: Series
  /** Zone usage of DTSTART, for the report. */
  startZone?: { key: string; tzid: string; zone: ResolvedZone }
  recurrenceId?: TimeValue
  cancelled: boolean
}

function readDuration(vevent: ICAL.Component): { nominalMs: number; exactMs: number } | null {
  const d = vevent.getFirstPropertyValue("duration") as ICAL.Duration | null
  if (!d || typeof d !== "object" || !("days" in d)) return null
  if (d.isNegative) return { nominalMs: 0, exactMs: 0 }
  return {
    nominalMs: ((d.weeks ?? 0) * 7 + (d.days ?? 0)) * DAY_MS,
    exactMs: ((d.hours ?? 0) * 3600 + (d.minutes ?? 0) * 60 + (d.seconds ?? 0)) * 1000,
  }
}

function untilWall(until: ICAL.Time, series: Series, ctx: FeedContext): number {
  const fields = Date.UTC(until.year, until.month - 1, until.day, until.hour, until.minute, until.second)
  if (until.isDate) {
    const day = Date.UTC(until.year, until.month - 1, until.day)
    // Inclusive: the whole UNTIL day for timed series
    return series.kind === "allday" ? day : day + DAY_MS - 1000
  }
  const isUtc = until.zone === ICAL.Timezone.utcTimezone || until.zone?.tzid === "UTC"
  if (!isUtc) return fields
  if (series.kind === "zoned") return instantToWall(fields, series.zone)
  return instantToWall(fields, series.kind === "allday" ? (ctx.calendarZone ?? ctx.userZone) : ctx.userZone)
}

function readEvent(vevent: ICAL.Component, ctx: FeedContext, skipped: SkippedEvent[]): ReadEvent {
  const summary = stringProp(vevent, "summary") ?? ""
  const status = stringProp(vevent, "status")?.toUpperCase()
  const cancelled = status === "CANCELLED" || status === "CANCELED"

  const dtstartProp = vevent.getFirstProperty("dtstart")
  if (!dtstartProp) throw new SkipError("no-start")
  const st = readTime(dtstartProp, dtstartProp.getFirstValue(), ctx)
  const uid = stringProp(vevent, "uid") ?? `generated-${st.wall}-${summary}`

  const dtendProp = vevent.getFirstProperty("dtend")
  const en = dtendProp ? readTime(dtendProp, dtendProp.getFirstValue(), ctx) : null

  const allDayHint =
    isTrueFlag(vevent.getFirstPropertyValue("x-microsoft-cdo-alldayevent")) ||
    isTrueFlag(vevent.getFirstPropertyValue("x-microsoft-msncalendar-alldayevent"))
  const allDay =
    st.isDate || (allDayHint && st.kind !== "utc" && isMidnight(st.wall) && (!en || isMidnight(en.wall)))

  let series: Series
  let startZone: ReadEvent["startZone"]
  if (allDay) {
    series = { kind: "allday" }
  } else if (st.kind === "utc") {
    series = { kind: "zoned", zone: "UTC", uncertain: false }
  } else if (st.kind === "zoned") {
    series = { kind: "zoned", zone: st.zone!.zone, uncertain: st.zone!.uncertain }
    startZone = { key: st.tzid!, tzid: st.tzid!, zone: st.zone! }
  } else if (ctx.calendarZone) {
    series = { kind: "zoned", zone: ctx.calendarZone, uncertain: false }
    startZone = {
      key: "X-WR-TIMEZONE",
      tzid: "X-WR-TIMEZONE",
      zone: { zone: ctx.calendarZone, source: "calendar", uncertain: false },
    }
  } else {
    series = { kind: "floating" }
  }

  // Start / end in series space (ms)
  let startMs: number
  let endMs: number
  const duration = readDuration(vevent)
  if (series.kind === "allday") {
    startMs = wallStartOfDay(st.wall)
    if (en) endMs = wallStartOfDay(en.wall)
    else if (duration) endMs = startMs + duration.nominalMs
    else endMs = startMs + DAY_MS
    if (endMs <= startMs) endMs = startMs + DAY_MS
  } else {
    startMs = series.kind === "zoned" ? (instantOf(st) ?? wallToInstant(st.wall, series.zone)) : st.wall
    if (en) {
      endMs = toSeriesMs(en, series, ctx)
    } else if (duration) {
      endMs =
        series.kind === "zoned"
          ? wallToInstant(st.wall + duration.nominalMs, series.zone) + duration.exactMs
          : st.wall + duration.nominalMs + duration.exactMs
    } else {
      endMs = startMs
    }
    if (endMs < startMs) endMs = startMs
  }

  const item: ICalEventItem = {
    uid,
    summary,
    description: stringProp(vevent, "description"),
    location: stringProp(vevent, "location"),
    start: Math.floor(startMs / 1000),
    end: Math.floor(endMs / 1000),
    allDay: series.kind === "allday",
    isFloating: series.kind === "floating",
  }
  if (series.kind === "zoned") {
    item.originalTzid = series.zone
    item.wallStart = Math.floor(st.wall / 1000)
    if (series.uncertain) item.tzUncertain = true
  }
  const organizer = stringProp(vevent, "organizer")
  if (organizer) item.organizer = organizer
  const attendees = vevent.getAllProperties("attendee").length
  if (attendees > 0) item.attendee_count = attendees

  // RECURRENCE-ID: a modified (or cancelled) instance of a series
  const ridProp = vevent.getFirstProperty("recurrence-id")
  const recurrenceId = ridProp ? readTime(ridProp, ridProp.getFirstValue(), ctx) : undefined

  if (!recurrenceId && !cancelled) {
    readRecurrence(vevent, item, series, st, ctx, skipped)
  }

  return { item, series, startZone, recurrenceId, cancelled }
}

function readRecurrence(
  vevent: ICAL.Component,
  item: ICalEventItem,
  series: Series,
  st: TimeValue,
  ctx: FeedContext,
  skipped: SkippedEvent[],
) {
  if (isTrueFlag(vevent.getFirstPropertyValue(RRULE_DROPPED.toLowerCase()))) {
    skipped.push({ uid: item.uid, summary: item.summary, reason: "invalid-rrule" })
  }
  const rruleProp = vevent.getFirstProperty("rrule")
  if (rruleProp) {
    try {
      const recur = rruleProp.getFirstValue() as ICAL.Recur
      const text = recur
        .toString()
        .split(";")
        .filter((part) => part && !/^UNTIL=/i.test(part))
        .join(";")
      const parsed = RRule.parseString(text)
      if (parsed.freq === undefined || parsed.freq === null) throw new Error("no FREQ")
      item.rrule = text
      if (recur.until) item.rruleUntil = Math.floor(untilWall(recur.until, series, ctx) / 1000)
    } catch {
      skipped.push({ uid: item.uid, summary: item.summary, reason: "invalid-rrule" })
    }
  }

  for (const prop of vevent.getAllProperties("exdate")) {
    for (const value of prop.getValues()) {
      try {
        addExclusion(item, readTime(prop, value, ctx), series, ctx)
      } catch {
        // An unreadable EXDATE value excludes nothing
      }
    }
  }

  // Time of day of DTSTART, for DATE-valued RDATEs on timed series
  const timeOfDay = st.wall - wallStartOfDay(st.wall)
  for (const prop of vevent.getAllProperties("rdate")) {
    for (const raw of prop.getValues()) {
      try {
        const value = raw && typeof raw === "object" && "start" in raw ? (raw as ICAL.Period).start : raw
        const tv = readTime(prop, value, ctx)
        let ms: number
        if (series.kind === "allday") {
          ms = wallStartOfDay(tv.isDate ? tv.wall : toSeriesMs(tv, series, ctx))
        } else if (tv.isDate) {
          const wall = tv.wall + timeOfDay
          ms = series.kind === "zoned" ? wallToInstant(wall, series.zone) : wall
        } else {
          ms = toSeriesMs(tv, series, ctx)
        }
        ;(item.rdates ??= []).push(Math.floor(ms / 1000))
      } catch {
        // An unreadable RDATE value adds nothing
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Feed-level syntax: parse the whole text, or block by block when that fails
// ---------------------------------------------------------------------------

interface ParsedRoot {
  root: ICAL.Component
  unparseable: number
}

/** Marks a VEVENT whose unparseable RRULE was removed by `parseBlockwise`. */
const RRULE_DROPPED = "X-TOTODO-RRULE-DROPPED"

/** The lines without property `name` (and its folded continuation lines). */
function withoutProperty(lines: string[], name: string): string[] {
  const out: string[] = []
  let dropping = false
  const prefix = new RegExp(`^${name}[;:]`, "i")
  for (const line of lines) {
    if (dropping && /^[ \t]/.test(line)) continue
    dropping = prefix.test(line)
    if (!dropping) out.push(line)
  }
  return out
}

function wrap(lines: string[]): string {
  return ["BEGIN:VCALENDAR", "VERSION:2.0", ...lines, "END:VCALENDAR"].join("\r\n")
}

/**
 * When ical.js rejects the whole text, split it into VTIMEZONE and VEVENT blocks and parse each on
 * its own, so one malformed event does not lose the feed.
 */
function parseBlockwise(text: string): ParsedRoot {
  const lines = text.split(/\r\n|\n|\r/)
  const header: string[] = []
  const blocks: { name: string; lines: string[] }[] = []
  let current: { name: string; lines: string[] } | null = null
  for (const line of lines) {
    const upper = line.trim().toUpperCase()
    if (!current) {
      if (upper === "BEGIN:VEVENT" || upper === "BEGIN:VTIMEZONE") {
        current = { name: upper.slice(6), lines: [line] }
      } else if (upper !== "BEGIN:VCALENDAR" && upper !== "END:VCALENDAR" && upper.startsWith("X-WR-")) {
        header.push(line)
      }
      continue
    }
    if (upper === "BEGIN:VEVENT" && current.name === "VEVENT") {
      // A block that never ended: close it as is (it will fail to parse) and start the next
      blocks.push(current)
      current = { name: "VEVENT", lines: [line] }
      continue
    }
    current.lines.push(line)
    if (upper === `END:${current.name}`) {
      blocks.push(current)
      current = null
    }
  }
  if (current) blocks.push(current)

  let root: ICAL.Component
  try {
    root = new ICAL.Component(ICAL.parse(wrap(header)))
  } catch {
    root = new ICAL.Component(ICAL.parse(wrap([])))
  }
  let unparseable = 0
  const parseBlock = (name: string, blockLines: string[]): ICAL.Component | null => {
    try {
      return new ICAL.Component(ICAL.parse(wrap(blockLines))).getFirstSubcomponent(name.toLowerCase())
    } catch {
      return null
    }
  }
  for (const block of blocks) {
    let comp = parseBlock(block.name, block.lines)
    if (!comp && block.name === "VEVENT") {
      // Often only the RRULE is malformed: keep the event, without its recurrence
      const withoutRule = withoutProperty(block.lines, "RRULE")
      if (withoutRule.length < block.lines.length) {
        comp = parseBlock(block.name, [...withoutRule.slice(0, -1), `${RRULE_DROPPED}:TRUE`, ...withoutRule.slice(-1)])
      }
    }
    if (comp) root.addSubcomponent(comp)
    else if (block.name === "VEVENT") unparseable++
  }
  return { root, unparseable }
}

/**
 * Quote TZID parameter values that contain ":" - Outlook writes `TZID=tzone://Microsoft/Utc` and
 * some exporters `TZID=(UTC+01:00) Amsterdam, ...` unquoted, which ical.js splits at the colon.
 */
export function quoteColonTzids(text: string): string {
  if (!/TZID=(?:[A-Za-z][\w+.-]*:\/\/|\()/i.test(text)) return text
  return text
    .replace(/;TZID=([A-Za-z][\w+.-]*:\/\/[^;:"\r\n]*)(?=[;:])/gi, ';TZID="$1"')
    .replace(/;TZID=(\((?:UTC|GMT)[^)\r\n]*\)[^;:"\r\n]*)(?=[;:])/gi, ';TZID="$1"')
}

function parseRoot(text: string): ParsedRoot {
  try {
    const jcal = ICAL.parse(text)
    // A text with several VCALENDARs parses to an array; use the first
    const first = Array.isArray(jcal) && Array.isArray(jcal[0]) ? jcal[0] : jcal
    return { root: new ICAL.Component(first), unparseable: 0 }
  } catch {
    return parseBlockwise(text)
  }
}

function utcOffsetMs(v: unknown): number | null {
  if (v && typeof v === "object" && "toSeconds" in v) {
    const seconds = (v as ICAL.UtcOffset).toSeconds()
    return Number.isFinite(seconds) ? seconds * 1000 : null
  }
  return null
}

function readObservance(sub: ICAL.Component): VTimezoneObservance | null {
  const dtstart = sub.getFirstPropertyValue("dtstart")
  const offsetFrom = utcOffsetMs(sub.getFirstPropertyValue("tzoffsetfrom"))
  const offsetTo = utcOffsetMs(sub.getFirstPropertyValue("tzoffsetto"))
  if (!isTime(dtstart) || offsetFrom === null || offsetTo === null) return null
  const wallOf = (t: ICAL.Time) => Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute, t.second)
  const ob: VTimezoneObservance = { start: wallOf(dtstart), offsetFrom, offsetTo }
  const rrule = sub.getFirstPropertyValue("rrule")
  if (rrule) ob.rrule = String(rrule)
  const rdates: number[] = []
  for (const prop of sub.getAllProperties("rdate")) {
    for (const v of prop.getValues()) if (isTime(v)) rdates.push(wallOf(v))
  }
  if (rdates.length > 0) ob.rdates = rdates
  return ob
}

function vtimezoneInfos(root: ICAL.Component, now: number): Map<string, VTimezoneInfo> {
  const map = new Map<string, VTimezoneInfo>()
  for (const comp of root.getAllSubcomponents("vtimezone")) {
    try {
      const tzid = stringProp(comp, "tzid")
      if (!tzid) continue
      const observances = [...comp.getAllSubcomponents("standard"), ...comp.getAllSubcomponents("daylight")]
        .map(readObservance)
        .filter((o): o is VTimezoneObservance => o !== null)
      const info = vtimezoneFromObservances(tzid, observances, now)
      if (info) map.set(tzidKey(tzid), info)
    } catch {
      // A VTIMEZONE that cannot be read cannot be matched; its TZID falls through to tier 4
    }
  }
  return map
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parse ICS text. Throws `ICalParseError` when the text is not an iCalendar feed at all; a feed
 * whose events are individually unreadable parses with those events in `skipped`.
 */
export function parseICalFeed(icsText: string, opts: ParseOptions = {}): ParsedFeed {
  const text = quoteColonTzids(icsText.startsWith("﻿") ? icsText.slice(1) : icsText)
  if (!/BEGIN:VCALENDAR/i.test(text)) {
    throw new ICalParseError("not-ical", "not an iCalendar feed")
  }
  let parsed: ParsedRoot
  try {
    parsed = parseRoot(text)
  } catch (e) {
    throw new ICalParseError("parse", e instanceof Error ? e.message : "unparseable feed")
  }
  const { root } = parsed

  const userZone = opts.userZone ?? viewerZone()
  const now = opts.now ?? Date.now()
  const calendarName =
    stringProp(root, "x-wr-calname")?.trim() || stringProp(root, "name")?.trim() || null
  const resolveCtx: ResolveContext = {
    vtimezones: vtimezoneInfos(root, now),
    calendarZone: stringProp(root, "x-wr-timezone"),
    userZone,
    now,
  }
  const ctx: FeedContext = {
    resolve: createZoneResolver(resolveCtx),
    calendarZone: resolveCalendarZone(resolveCtx, now),
    userZone,
  }

  const skipped: SkippedEvent[] = []
  for (let i = 0; i < parsed.unparseable; i++) skipped.push({ reason: "unparseable" })

  const bases: ReadEvent[] = []
  const overrides: ReadEvent[] = []
  for (const vevent of root.getAllSubcomponents("vevent")) {
    try {
      const ev = readEvent(vevent, ctx, skipped)
      if (ev.recurrenceId) overrides.push(ev)
      else if (!ev.cancelled) bases.push(ev)
    } catch (e) {
      let uid: string | undefined
      let summary: string | undefined
      try {
        uid = stringProp(vevent, "uid") ?? undefined
        summary = stringProp(vevent, "summary") ?? undefined
      } catch {
        // nothing more to report
      }
      skipped.push({ uid, summary, reason: e instanceof SkipError ? e.reason : "error" })
    }
  }

  // Pass 2: modified and cancelled instances
  const basesByUid = new Map<string, ReadEvent[]>()
  for (const b of bases) {
    const list = basesByUid.get(b.item.uid)
    if (list) list.push(b)
    else basesByUid.set(b.item.uid, [b])
  }
  const kept: ReadEvent[] = [...bases]
  for (const ov of overrides) {
    const owners = basesByUid.get(ov.item.uid) ?? []
    for (const owner of owners) {
      try {
        addExclusion(owner.item, ov.recurrenceId!, owner.series, ctx)
      } catch {
        // An unreadable RECURRENCE-ID excludes nothing
      }
    }
    if (ov.cancelled) continue
    const owner = owners[0]
    if (owner) {
      try {
        ov.item.recurrenceId = Math.floor(toSeriesMs(ov.recurrenceId!, owner.series, ctx) / 1000)
      } catch {
        // informational only
      }
    }
    // An orphan (series not in the feed) is shown as the single event it describes
    kept.push(ov)
  }

  // Report
  const zones = new Map<string, ZoneReport>()
  let recurringCount = 0
  for (const ev of kept) {
    if (!ev.recurrenceId && (ev.item.rrule || ev.item.rdates?.length)) recurringCount++
    if (!ev.startZone) continue
    const report = zones.get(ev.startZone.key)
    if (report) report.events++
    else {
      zones.set(ev.startZone.key, {
        tzid: ev.startZone.tzid,
        zone: ev.startZone.zone.zone,
        source: ev.startZone.zone.source,
        uncertain: ev.startZone.zone.uncertain,
        events: 1,
      })
    }
  }

  return {
    events: kept.map((e) => e.item),
    calendarName,
    calendarZone: ctx.calendarZone,
    skipped,
    eventCount: bases.length,
    recurringCount,
    zones: [...zones.values()],
  }
}

/** The events of a feed, or `[]` when it cannot be read (tests, quick checks). */
export function parseICalData(icsText: string, opts?: ParseOptions): ICalEventItem[] {
  try {
    return parseICalFeed(icsText, opts).events
  } catch {
    return []
  }
}

